import {AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';

type EUDocumentPatternKey =
  | 'numericStandard'
  | 'alphaLeading'
  | 'alphaTrailing'
  | 'complexAlphanumeric'
  | 'passportStandard';

export class CheckInValidators {
  static documentNumberByType(typeControlName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent) return null;

      const rawValue = control.value;
      const rawType = control.parent.get(typeControlName)?.value;

      const value = String(rawValue || '').trim().toUpperCase();
      const type = String(rawType || '').trim().toUpperCase();

      if (!value || !type) return null;

      switch (type) {
        case 'D':
          return CheckInValidators.isValidDni(value) ? null : { invalidDni: true };

        case 'N': // Los permisos de residencia usan el formato NIE
          return CheckInValidators.isValidNie(value) ? null : { invalidNie: true };

        case 'I':
          return CheckInValidators.isValidEuropeanDocument(value) ? null : { invalidDocument: true };

        case 'P':
          return /^[A-Z0-9]{6,20}$/.test(value) ? null : { invalidPassport: true };

        default:
          return null;
      }
    };
  }

  private static requiredWhenValueMatches(
    controlName: string,
    targetValues: string[],
    condition: (index: number, hasControlValue: boolean) => boolean
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent) return null;

      const rawType = control.parent.get(controlName)?.value;
      const type = String(rawType || '').trim().toUpperCase();
      const value = String(control.value || '').trim();
      const normalizedTargets = targetValues.map(item => String(item || '').trim().toUpperCase());

      const index = normalizedTargets.indexOf(type);
      if (!condition(index, !!type)) {
        return null;
      }

      return value ? null : { required: true };
    };
  }

  static requiredWhenDocumentTypeIs(typeControlName: string, requiredTypes: string[]): ValidatorFn {
    return CheckInValidators.requiredWhenValueMatches(
      typeControlName,
      requiredTypes,
      index => index !== -1
    );
  }

  static requiredWhenCountryIs(countryControlName: string, requiredCountries: string[]): ValidatorFn {
    return CheckInValidators.requiredWhenValueMatches(
      countryControlName,
      requiredCountries,
      index => index !== -1
    );
  }

  static requiredWhenCountryIsNot(countryControlName: string, excludedCountries: string[]): ValidatorFn {
    return CheckInValidators.requiredWhenValueMatches(
      countryControlName,
      excludedCountries,
      (index, hasValue) => hasValue && index === -1
    );
  }

  static strictEmail(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      var value = String(control.value || '').trim();

      if (!value) return null;

      var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
      return emailRegex.test(value) ? null : {invalidFormat: true};
    };
  }

  private static checkDniNieChecksum(numericPart: string, letter: string): boolean {
    const number = parseInt(numericPart, 10);
    const letters = 'TRWAGMYFPDXBNJZSQVHLCKE';
    return letters.charAt(number % 23) === letter;
  }

  private static isValidDni(value: string): boolean {
    var match = value.match(/^(\d{8})([A-Z])$/);
    if (!match) return false;
    return CheckInValidators.checkDniNieChecksum(match[1], match[2]);
  }

  private static isValidNie(value: string): boolean {
    var match = value.match(/^([XYZ])(\d{7})([A-Z])$/);
    if (!match) return false;

    var prefixMap: {[key: string]: string} = {
      X: '0',
      Y: '1',
      Z: '2'
    };

    return CheckInValidators.checkDniNieChecksum(prefixMap[match[1]] + match[2], match[3]);
  }

  private static isValidEuropeanDocument(value: string) {
    if (!value) {
      return false;
    }

    const rawValue = value.trim();

    if (rawValue.length < 5 || rawValue.length > 25) {
      return false;
    }

    if (/^[0-9]{3}-[0-9]{7}-[0-9]{2}$/.test(rawValue)) {
      return true;
    }

    if (/^[0-9]{2}\.[0-9]{2}\.[0-9]{2}-[0-9]{3}\.[0-9]{2}$/.test(rawValue)) {
      return true;
    }

    const normalizedValue = rawValue.replace(/[\s.\-\/]/g, '').toUpperCase();

    const patterns: Record<EUDocumentPatternKey, RegExp> = {
      numericStandard: /^[0-9]{9,12}$/,
      alphaLeading: /^[A-Z]{1,2}[0-9]{6,9}$/,
      alphaTrailing: /^[0-9]{6,8}[A-Z]{1,2}$/,
      complexAlphanumeric: /^[A-Z0-9]{8,16}$/,
      passportStandard: /^[A-Z0-9]{7,10}$/
    };

    return Object.values(patterns).some(pattern => pattern.test(normalizedValue));
  }
}
